import { useRef, type KeyboardEvent } from 'react';
import type { Size } from '../engine/types';
import { useI18n } from '../i18n';
import { isOneSizeName } from '../lib/sizes';

/** Radio group of soft pills (roving tabindex, arrow keys): "all sizes" + one pill per size. */
export function SizeChips({
  sizes,
  selected,
  onSelect,
  withAll = true,
  label,
  testId = 'size-chips',
}: {
  sizes: Size[];
  selected: number | null;
  onSelect: (s: number | null) => void;
  withAll?: boolean;
  /** Visible label before the pills (defaults to a hidden one). */
  label?: string;
  testId?: string;
}) {
  const { t, lang } = useI18n();
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const options: Array<{ value: number | null; label: string }> = [
    ...(withAll ? [{ value: null, label: t('top.allSizes') }] : []),
    ...sizes.map((s) => ({ value: s.index, label: isOneSizeName(s.name) ? (lang === 'en' ? 'one size' : 'taille unique') : s.name })),
  ];
  const current = Math.max(
    0,
    options.findIndex((o) => o.value === selected),
  );
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const delta = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
    if (!delta) return;
    e.preventDefault();
    const next = (index + delta + options.length) % options.length;
    onSelect(options[next].value);
    refs.current[next]?.focus();
  };
  if (sizes.length === 0) return <div data-testid={testId} />;
  return (
    <div className="flex max-w-full items-center gap-2">
      {label && (
        <span className="kicker shrink-0" aria-hidden="true">
          {label}
        </span>
      )}
      <div
        role="radiogroup"
        aria-label={label ?? t('top.sizesAria')}
        className="flex max-w-full items-center gap-0.5 overflow-x-auto rounded-pill bg-sunken p-0.5"
        data-testid={testId}
      >
        {options.map((o, i) => {
          const active = i === current;
          return (
            <button
              key={o.value ?? 'all'}
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              onClick={() => onSelect(o.value)}
              onKeyDown={(e) => onKeyDown(e, i)}
              className={`h-7 min-w-[2.25rem] shrink-0 whitespace-nowrap rounded-pill px-3 text-xs font-semibold transition-colors ${
                active ? 'bg-surface text-fg shadow-soft-sm' : 'text-muted hover:text-fg'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
